import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const projects = [
  {
    title: 'Kognita VR Edu',
    category: 'Cold Email + Lead Gen',
    hook: 'Ok. 2 tys. cold maili → 2 pozyskanych klientów, kilkukrotny ROI',
    image: '/images/works/kognita-v2.jpg',
    href: '/blog',
    colSpan: 'md:col-span-7'
  },
  {
    title: 'Klient e-commerce 3-spółkowy',
    category: 'Customer Support RAG (PL/DE/HK)',
    hook: '1 200 ticketów / m-c → 95% automatyzacji',
    image: '/images/works/ecom-3-v2.jpg',
    href: '/blog/ecom-rag-customer-support',
    colSpan: 'md:col-span-5'
  },
  {
    title: 'Firma deweloperska Trójmiasto',
    category: 'Workflow Automation n8n',
    hook: 'Z chaosu Excela do automatu LinkedIn → Pipedrive',
    image: '/images/works/deweloperka-v2.jpg',
    href: '/blog/voice-agent-case-study-deweloperka',
    colSpan: 'md:col-span-5'
  },
  {
    title: 'DAIEN AIOs',
    category: 'Multi-Agent System',
    hook: 'Agenci AI na produkcji w Discord - codziennie używam',
    image: '/images/works/daien-aios-v2.jpg',
    href: '/uslugi',
    colSpan: 'md:col-span-7'
  }
];

export default function SelectedWorks() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="bg-bg py-16 md:py-24" ref={ref} id="case-studies" aria-label="Case Studies">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16">

        {/* Header */}
        <motion.div
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-8 h-px bg-stroke"></div>
              <span className="text-xs text-muted uppercase tracking-[0.3em]">Realizacje</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-text-primary mb-4">
              Cztery systemy które działają <span className="font-display italic">w produkcji</span>
            </h2>
            <p className="text-muted max-w-sm">Realne projekty, realne liczby, realni klienci. Bez zmyślonych metryk.</p>
          </div>

          <Link to="/blog" className="hidden md:inline-flex border-gradient items-center gap-2 bg-surface text-text-primary rounded-full px-6 py-3 hover:bg-bg transition-colors group">
            <span className="relative z-10 font-medium text-sm">Zobacz wszystkie</span>
            <ArrowRight className="relative z-10 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-3 md:gap-6">
          {projects.map((project, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 1, delay: 0.1 * idx, ease: "easeOut" }}
              className={project.colSpan}
            >
              <Link
                to={project.href}
                className="relative overflow-hidden bg-surface border border-stroke rounded-3xl group cursor-pointer aspect-[4/3] md:aspect-auto md:min-h-[400px] block h-full"
              >
                {/* Background Image */}
                <img
                  src={project.image}
                  alt={`${project.title} - ${project.category}`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />

                {/* Halftone Overlay */}
                <div
                  className="absolute inset-0 opacity-20 mix-blend-multiply pointer-events-none"
                  style={{ backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)', backgroundSize: '4px 4px' }}
                  aria-hidden="true"
                />

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-bg/70 opacity-0 group-hover:opacity-100 backdrop-blur-lg transition-all duration-500 flex flex-col justify-center items-center p-6 text-center">

                  <div className="border-gradient rounded-full p-[1px] transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 delay-100 mb-4">
                    <div className="bg-text-primary text-bg px-6 py-3 rounded-full flex items-center gap-2">
                      <span className="font-medium">Pełna historia -</span>
                      <span className="font-display italic text-lg leading-none">{project.title}</span>
                    </div>
                  </div>

                  <p className="text-text-primary text-sm md:text-base max-w-md opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-200">
                    {project.hook}
                  </p>

                </div>

                {/* Permanent Label (visible before hover) */}
                <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end transition-opacity duration-300 group-hover:opacity-0">
                  <div className="bg-bg/60 backdrop-blur-md px-4 py-2 rounded-xl border border-stroke">
                    <h3 className="text-text-primary font-medium">{project.title}</h3>
                    <p className="text-xs text-muted mt-1">{project.category}</p>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <Link to="/blog" className="mt-10 w-full md:hidden border-gradient inline-flex items-center justify-center gap-2 bg-surface text-text-primary rounded-full px-6 py-4 hover:bg-bg transition-colors group">
          <span className="relative z-10 font-medium">Zobacz wszystkie</span>
          <ArrowRight className="relative z-10 w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </section>
  );
}
